/**
 * ONE READ, FOUR STATES, AND A RETRY THAT REALLY RETRIES.
 *
 * WHY THIS EXISTS RATHER THAN TanStack Query. ADR-0007 rules TanStack Query for the cabinet, and
 * it is the right answer — but `@tanstack/react-query` is not a declared dependency of
 * `web/authors` and is not installed (`web/authors/node_modules` holds `@mantine`, `@moonx`,
 * `@types`, `@vitejs`, `react`, `react-dom`, `vite`; `web/ui` has `@tanstack/react-table` and
 * `@tanstack/store`, not Query). Adding it means editing a package.json this track does not own,
 * so it is reported as a needed dependency instead.
 *
 * This hook is therefore a STAND-IN with the same shape as one `useQuery`: a discriminated state,
 * a `reload()`, and a `reloading` flag so a refresh does not blank a page that already has content.
 * When Query lands, each `useAsyncResource(...)` call site becomes a `useQuery(...)` and the
 * components above it do not change — they read `status`, `data`, `error`, `reload`, `reloading`,
 * all of which Query supplies under its own names.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: cache, dedupe or refetch on focus. Both of this feature's GETs
 * declare `Cache-Control: no-store` and there is no CDN in front (ADR-0002), so a cache here would
 * be a second, undocumented one. One operator, ~530 authors — a help page does not need a cache.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ApiError } from '../api/errors';
import { toApiError } from '../api/errors';

export type AsyncStatus = 'loading' | 'ready' | 'failed';

export interface AsyncResource<T> {
  readonly status: AsyncStatus;
  /** Non-null exactly when `status === 'ready'`, and kept across a `reload()`. */
  readonly data: T | null;
  /** Non-null exactly when `status === 'failed'`. */
  readonly error: ApiError | null;
  /** True while a `reload()` is in flight over content that is already on screen. */
  readonly reloading: boolean;
  readonly reload: () => void;
}

interface InternalState<T> {
  readonly status: AsyncStatus;
  readonly data: T | null;
  readonly error: ApiError | null;
  readonly reloading: boolean;
}

const INITIAL: InternalState<never> = {
  status: 'loading',
  data: null,
  error: null,
  reloading: false,
};

/**
 * @param read  the operation to run. It MUST be stable — wrap it in `useCallback` at the call site,
 *              because a new function identity restarts the read.
 * @param enabled  when false the resource sits in `loading` and issues nothing. Used by screens
 *              that already have the data from the shell.
 */
export function useAsyncResource<T>(
  read: (signal: AbortSignal) => Promise<T>,
  enabled = true,
): AsyncResource<T> {
  const [state, setState] = useState<InternalState<T>>(INITIAL);
  // Bumping this re-runs the effect. A counter rather than a boolean so two rapid retries are two
  // reads and not one.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const controller = new AbortController();
    let live = true;

    // A reload over content that is already on screen shows a quiet busy flag rather than tearing
    // the page down to a skeleton. Read through the updater so the effect does not have to depend
    // on `state` — depending on it would restart the read on its own result.
    setState((previous) =>
      previous.data === null ? previous : { ...previous, reloading: true },
    );

    read(controller.signal)
      .then((data) => {
        // StrictMode double-invokes effects in development. `live` is what makes the discarded run
        // discard its result instead of racing the kept one.
        if (live) {
          setState({ status: 'ready', data, error: null, reloading: false });
        }
      })
      .catch((thrown: unknown) => {
        if (!live) {
          return;
        }
        const error = toApiError(thrown);
        // An abort is this component tearing down, not a failure to show anybody.
        if (controller.signal.aborted) {
          return;
        }
        setState({ status: 'failed', data: null, error, reloading: false });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [read, attempt, enabled]);

  const reload = useCallback(() => {
    setAttempt((previous) => previous + 1);
  }, []);

  return {
    status: state.status,
    data: state.data,
    error: state.error,
    reloading: state.reloading,
    reload,
  };
}
